import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  RefreshCw,
  Link,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Check,
  X,
  Loader2,
  Settings,
  Cloud,
  CloudOff,
  History,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Download,
  Minus,
  ExternalLink,
  Zap,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const APISyncTab = () => {
  const { language } = useLanguage();
  
  // State
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  
  // Form state
  const [newProvider, setNewProvider] = useState({
    name: '',
    api_url: '',
    owner_email: '',
    api_format: 'auto',
    auth_type: 'none',
    auth_value: ''
  });
  
  // Selection state for applying changes
  const [selectedChanges, setSelectedChanges] = useState(new Set());
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [changeOwner, setChangeOwner] = useState(true);
  const [enableSync, setEnableSync] = useState(true);
  
  // Excel upload state
  const [uploadedExcelData, setUploadedExcelData] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [excelUpdateResult, setExcelUpdateResult] = useState(null);
  const [selectedExcelChanges, setSelectedExcelChanges] = useState(new Set());
  const [applyingExcelChanges, setApplyingExcelChanges] = useState(false);
  const [excelPreviewData, setExcelPreviewData] = useState(null);
  const [excelSelected, setExcelSelected] = useState(new Set());
  const [syncNotifications, setSyncNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [runningManualSync, setRunningManualSync] = useState(false);
  const [excelChangeOwner, setExcelChangeOwner] = useState(true);
  const [excelApiOwnerEmail, setExcelApiOwnerEmail] = useState('');
  const [lastSyncId, setLastSyncId] = useState(null);  // For download after apply
  const fileInputRef = useRef(null);

  const texts = {
    es: {
      title: 'Sincronización de APIs',
      description: 'Conecta APIs de proveedores para comparar y sincronizar precios',
      addProvider: 'Añadir Proveedor',
      providers: 'Proveedores',
      comparison: 'Comparación',
      history: 'Historial',
      name: 'Nombre',
      apiUrl: 'URL de la API',
      ownerEmail: 'Email del Proveedor',
      testConnection: 'Probar Conexión',
      compare: 'Comparar Precios',
      noProviders: 'No hay proveedores configurados',
      addFirst: 'Añade tu primer proveedor de API',
      products: 'productos',
      moreExpensive: 'Más caros en tu APP',
      cheaper: 'Más baratos en tu APP',
      samePrice: 'Mismo precio',
      onlyInApi: 'Solo en la API',
      domain: 'Dominio',
      yourPrice: 'Tu Precio',
      apiPrice: 'Precio API',
      difference: 'Diferencia',
      currentOwner: 'Dueño Actual',
      applySelected: 'Aplicar Seleccionados',
      selectAll: 'Seleccionar Todo',
      potentialSavings: 'Ahorro potencial',
      autoSync: 'Sync Automático',
      lastSync: 'Última sincronización',
      enableAutoSync: 'Habilitar sync diario',
      changeOwnerToo: 'Cambiar también el dueño',
      markForSync: 'Marcar para sync automático',
      applyChanges: 'Aplicar Cambios',
      cancel: 'Cancelar',
      save: 'Guardar',
      delete: 'Eliminar',
      confirmDelete: '¿Eliminar este proveedor?',
      confirmDeleteDesc: 'Se eliminarán todas las configuraciones de sincronización asociadas.',
      testing: 'Probando conexión...',
      testSuccess: 'Conexión exitosa',
      testFailed: 'Error de conexión',
      syncing: 'Sincronizando...',
      syncComplete: 'Sincronización completada',
      noChangesSelected: 'No hay cambios seleccionados',
      connectionOk: 'Conexión OK',
      uploadExcel: 'Subir Excel',
      uploadExcelDesc: 'Sube un archivo Excel de comparación para revisar y aplicar cambios',
      excelReview: 'Revisión Excel',
      uploadFile: 'Subir Archivo',
      selectFile: 'Seleccionar archivo Excel...',
      processingFile: 'Procesando archivo...',
      productsToChange: 'Productos a Cambiar',
      productsToKeep: 'Productos a Mantener',
      newProducts: 'Productos Nuevos (solo en API)',
      applySelectedChanges: 'Aplicar Cambios Seleccionados',
      apiOwnerEmail: 'Email del proveedor API',
      notInDatabase: 'No encontrado en BD',
      foundInDatabase: 'Encontrado',
    },
    en: {
      title: 'API Synchronization',
      description: 'Connect provider APIs to compare and sync prices',
      addProvider: 'Add Provider',
      providers: 'Providers',
      comparison: 'Comparison',
      history: 'History',
      name: 'Name',
      apiUrl: 'API URL',
      ownerEmail: 'Provider Email',
      testConnection: 'Test Connection',
      compare: 'Compare Prices',
      noProviders: 'No providers configured',
      addFirst: 'Add your first API provider',
      products: 'products',
      moreExpensive: 'More expensive in your APP',
      cheaper: 'Cheaper in your APP',
      samePrice: 'Same price',
      onlyInApi: 'Only in API',
      domain: 'Domain',
      yourPrice: 'Your Price',
      apiPrice: 'API Price',
      difference: 'Difference',
      currentOwner: 'Current Owner',
      applySelected: 'Apply Selected',
      selectAll: 'Select All',
      potentialSavings: 'Potential savings',
      autoSync: 'Auto Sync',
      lastSync: 'Last sync',
      enableAutoSync: 'Enable daily sync',
      changeOwnerToo: 'Also change owner',
      markForSync: 'Mark for auto sync',
      applyChanges: 'Apply Changes',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      confirmDelete: 'Delete this provider?',
      confirmDeleteDesc: 'All associated sync configurations will be removed.',
      testing: 'Testing connection...',
      testSuccess: 'Connection successful',
      testFailed: 'Connection error',
      syncing: 'Syncing...',
      syncComplete: 'Sync complete',
      noChangesSelected: 'No changes selected',
      connectionOk: 'Connection OK',
      uploadExcel: 'Upload Excel',
      uploadExcelDesc: 'Upload a comparison Excel file to review and apply changes',
      excelReview: 'Excel Review',
      uploadFile: 'Upload File',
      selectFile: 'Select Excel file...',
      processingFile: 'Processing file...',
      productsToChange: 'Products to Change',
      productsToKeep: 'Products to Keep',
      newProducts: 'New Products (API only)',
      applySelectedChanges: 'Apply Selected Changes',
      apiOwnerEmail: 'API provider email',
      notInDatabase: 'Not found in DB',
      foundInDatabase: 'Found',
    }
  };
  
  const t = texts[language] || texts.es;

  // Fetch providers
  const fetchProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api-sync/providers`);
      setProviders(response.data.providers || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api-sync/sync-history`);
      setSyncHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
    fetchHistory();
    fetchNotifications();
  }, [fetchProviders, fetchHistory]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/api-sync/sync-notifications`);
      setSyncNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleManualSync = async () => {
    setRunningManualSync(true);
    try {
      const response = await axios.post(`${API}/api-sync/run-sync`, {}, { timeout: 300000 });
      toast.success(`Sync completado: ${response.data.synced} actualizados, ${response.data.deactivated} desactivados`);
      fetchNotifications();
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en sincronización');
    } finally {
      setRunningManualSync(false);
    }
  };

  const markNotificationsRead = async () => {
    try {
      await axios.post(`${API}/api-sync/sync-notifications/mark-read`);
      setUnreadCount(0);
      fetchNotifications();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Add provider
  const handleAddProvider = async () => {
    if (!newProvider.name || !newProvider.api_url || !newProvider.owner_email) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }
    
    try {
      await axios.post(`${API}/api-sync/providers`, newProvider);
      toast.success('Proveedor añadido correctamente');
      setAddDialogOpen(false);
      setNewProvider({ name: '', api_url: '', owner_email: '', api_format: 'auto', auth_type: 'none', auth_value: '' });
      fetchProviders();
    } catch (error) {
      console.error('Error adding provider:', error);
      toast.error(error.response?.data?.detail || 'Error al añadir proveedor');
    }
  };

  // Delete provider
  const handleDeleteProvider = async () => {
    if (!providerToDelete) return;
    
    try {
      await axios.delete(`${API}/api-sync/providers/${providerToDelete}`);
      toast.success('Proveedor eliminado');
      setDeleteDialogOpen(false);
      setProviderToDelete(null);
      if (selectedProvider === providerToDelete) {
        setSelectedProvider(null);
        setComparison(null);
      }
      fetchProviders();
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast.error('Error al eliminar proveedor');
    }
  };

  // Test connection
  const handleTestConnection = async (providerId) => {
    try {
      const response = await axios.post(`${API}/api-sync/providers/${providerId}/test`);
      if (response.data.success) {
        toast.success(`${t.testSuccess}: ${response.data.products_count} ${t.products}`);
      } else {
        toast.error(`${t.testFailed}: ${response.data.message}`);
      }
      fetchProviders();
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error(t.testFailed);
    }
  };

  // Compare prices
  const handleCompare = async (providerId) => {
    setComparing(true);
    setSelectedProvider(providerId);
    setSelectedChanges(new Set());
    setComparison(null);
    
    try {
      // Start background comparison
      await axios.post(`${API}/api-sync/providers/${providerId}/compare`, {}, { timeout: 15000 });
      
      // Poll for result
      const pollResult = async () => {
        for (let i = 0; i < 60; i++) {  // Max 2 minutes
          await new Promise(r => setTimeout(r, 2000));
          try {
            const status = await axios.get(`${API}/api-sync/compare-status`, { timeout: 10000 });
            if (!status.data.in_progress) {
              if (status.data.error) {
                toast.error(status.data.error);
                setComparison(null);
              } else if (status.data.result) {
                setComparison(status.data.result);
                toast.success(language === 'es' ? 'Comparación completada' : 'Comparison completed');
              }
              return;
            }
          } catch (e) {
            // Network error during poll, keep trying
          }
        }
        toast.error(language === 'es' ? 'La comparación tardó demasiado' : 'Comparison took too long');
      };
      
      await pollResult();
    } catch (error) {
      console.error('Error comparing:', error);
      toast.error(error.response?.data?.detail || 'Error al comparar precios');
      setComparison(null);
    } finally {
      setComparing(false);
    }
  };

  // Download comparison Excel — bucket: 'expensive' | 'cheaper' | 'missing'
  const handleDownloadExcel = async (providerId, bucket = 'expensive') => {
    const labels = { expensive: 'mas caros', cheaper: 'mas baratos', missing: 'que no tengo' };
    try {
      toast.info(`Generando Excel "${labels[bucket]}"...`);
      const response = await axios.get(
        `${API}/api-sync/providers/${providerId}/compare-excel`,
        {
          params: { bucket },
          responseType: 'blob',
          timeout: 180000,
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const provider = providers.find(p => p.provider_id === providerId);
      const fileLabel = { expensive: 'mas_caros', cheaper: 'mas_baratos', missing: 'que_no_tengo' }[bucket];
      link.setAttribute(
        'download',
        `${fileLabel}_${provider?.name || 'api'}_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`Excel "${labels[bucket]}" descargado`);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      const detail = error.response?.data?.detail
        || error.response?.statusText
        || 'Error al descargar Excel';
      toast.error(detail);
    }
  };

  // Import "missing" (only_in_api) products into the catalog
  // domains: array of specific domains, or null for ALL missing
  const [importingMissing, setImportingMissing] = useState(false);
  const handleImportMissing = async (providerId, domains = null, opts = {}) => {
    const isAll = !domains || domains.length === 0;
    if (isAll) {
      const total = comparison?.summary?.only_in_api_count || 0;
      const ok = window.confirm(
        language === 'es'
          ? `Vas a importar TODOS los productos que la API tiene y tu catálogo no (≈${total}). Las categorías se mapearán a las que ya tienes; si no se encuentran, se intentará con IA. PVP automático. ¿Continuar?`
          : `You are about to import ALL products in the API but not in your catalog (~${total}). Categories will be mapped to your existing ones; if not found, AI will try. Auto PVP. Continue?`
      );
      if (!ok) return;
    }
    setImportingMissing(true);
    try {
      toast.info(
        language === 'es'
          ? 'Importando productos... esto puede tardar varios minutos si son muchos.'
          : 'Importing products... this may take several minutes for large catalogs.'
      );
      const response = await axios.post(
        `${API}/api-sync/providers/${providerId}/import-missing`,
        {
          all: isAll,
          domains: isAll ? null : domains,
          use_ai_for_unknown: opts.use_ai !== false,
          enable_auto_sync: opts.enable_sync !== false,
        },
        { timeout: 0 }
      );
      const r = response.data;
      toast.success(
        language === 'es'
          ? `Importados: ${r.imported} | IA usada: ${r.ai_used} (sin match: ${r.ai_unmatched})`
          : `Imported: ${r.imported} | AI used: ${r.ai_used} (no match: ${r.ai_unmatched})`
      );
      // Refresh comparison so the row disappears
      handleCompare(providerId);
    } catch (error) {
      console.error('Error importing missing:', error);
      toast.error(error.response?.data?.detail || 'Error al importar productos');
    } finally {
      setImportingMissing(false);
    }
  };

  // Toggle auto sync
  const handleToggleAutoSync = async (providerId, enabled) => {
    try {
      const endpoint = enabled ? 'enable-auto-sync' : 'disable-auto-sync';
      await axios.post(`${API}/api-sync/providers/${providerId}/${endpoint}`);
      toast.success(enabled ? 'Sincronización automática habilitada' : 'Sincronización automática deshabilitada');
      fetchProviders();
    } catch (error) {
      console.error('Error toggling auto sync:', error);
      toast.error('Error al cambiar configuración');
    }
  };

  // Apply selected changes
  const handleApplyChanges = async () => {
    if (selectedChanges.size === 0) {
      toast.error(t.noChangesSelected);
      return;
    }
    
    setApplyingChanges(true);
    
    try {
      const changes = Array.from(selectedChanges).map(productId => {
        const item = comparison.more_expensive.find(x => x.product_id === productId);
        return {
          product_id: productId,
          new_price: item?.api_price,
          change_owner: changeOwner,
          enable_sync: enableSync
        };
      });
      
      const response = await axios.post(`${API}/api-sync/providers/${selectedProvider}/apply-changes`, {
        changes,
        provider_id: selectedProvider
      });
      
      toast.success(`${response.data.updated} productos actualizados`);
      setSelectedChanges(new Set());
      handleCompare(selectedProvider); // Refresh comparison
      fetchHistory();
    } catch (error) {
      console.error('Error applying changes:', error);
      toast.error('Error al aplicar cambios');
    } finally {
      setApplyingChanges(false);
    }
  };

  // Upload Excel file
  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('El archivo debe ser Excel (.xlsx o .xls)');
      return;
    }
    
    setUploadingExcel(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('Uploading Excel to:', `${API}/api-sync/upload-comparison-excel`);
      
      const response = await axios.post(`${API}/api-sync/upload-comparison-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000  // 60 second timeout for large files
      });
      
      console.log('Upload response:', response.data);
      
      setUploadedExcelData(response.data);
      setSelectedExcelChanges(new Set());
      
      // Auto-detect API owner email from first product
      if (response.data.products_to_change?.length > 0) {
        const firstOwner = response.data.products_to_change[0]?.api_owner;
        if (firstOwner) {
          setExcelApiOwnerEmail(firstOwner);
        }
      }
      
      const msg = language === 'es' 
        ? `Excel procesado: ${response.data.summary.to_change} productos para cambiar`
        : `Excel processed: ${response.data.summary.to_change} products to change`;
      toast.success(msg);
    } catch (error) {
      console.error('Error uploading Excel:', error);
      const errorMsg = error.response?.data?.detail 
        || error.message 
        || (language === 'es' ? 'Error al procesar el archivo' : 'Error processing file');
      toast.error(errorMsg);
    } finally {
      setUploadingExcel(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Apply Excel changes
  const handleApplyExcelChanges = async () => {
    if (selectedExcelChanges.size === 0) {
      toast.error(t.noChangesSelected);
      return;
    }
    
    setApplyingExcelChanges(true);
    
    try {
      const productsToUpdate = uploadedExcelData.products_to_change
        .filter(p => selectedExcelChanges.has(p.domain))
        .map(p => ({
          domain: p.domain,
          product_id: p.product_id,
          new_price: p.api_price,
          local_price: p.local_price,
          current_owner: p.current_owner,
          api_owner: p.api_owner || excelApiOwnerEmail
        }));
      
      const response = await axios.post(`${API}/api-sync/apply-changes-bulk`, {
        products: productsToUpdate,
        api_owner_email: excelApiOwnerEmail,
        change_owner: excelChangeOwner,
        enable_sync: false,
        provider_id: null
      });
      
      // Save sync_id for download
      if (response.data.sync_id) {
        setLastSyncId(response.data.sync_id);
      }
      
      const msg = language === 'es' 
        ? `${response.data.updated} productos actualizados. Puedes descargar el Excel con los cambios.`
        : `${response.data.updated} products updated. You can download the Excel with changes.`;
      toast.success(msg);
      
      // Update local state to reflect changes
      setUploadedExcelData(prev => ({
        ...prev,
        products_to_change: prev.products_to_change.filter(p => !selectedExcelChanges.has(p.domain)),
        summary: {
          ...prev.summary,
          to_change: prev.summary.to_change - selectedExcelChanges.size
        }
      }));
      setSelectedExcelChanges(new Set());
      fetchHistory();
    } catch (error) {
      console.error('Error applying changes:', error);
      toast.error('Error al aplicar cambios');
    } finally {
      setApplyingExcelChanges(false);
    }
  };

  // Select/deselect all Excel changes
  const handleSelectAllExcel = () => {
    if (!uploadedExcelData?.products_to_change) return;
    
    const validProducts = uploadedExcelData.products_to_change.filter(p => p.product_id);
    if (selectedExcelChanges.size === validProducts.length) {
      setSelectedExcelChanges(new Set());
    } else {
      setSelectedExcelChanges(new Set(validProducts.map(p => p.domain)));
    }
  };

  // Toggle single Excel selection
  const toggleExcelSelection = (domain) => {
    const newSelected = new Set(selectedExcelChanges);
    if (newSelected.has(domain)) {
      newSelected.delete(domain);
    } else {
      newSelected.add(domain);
    }
    setSelectedExcelChanges(newSelected);
  };

  // Select/deselect all
  const handleSelectAll = (items) => {
    if (selectedChanges.size === items.length) {
      setSelectedChanges(new Set());
    } else {
      setSelectedChanges(new Set(items.map(x => x.product_id)));
    }
  };


  // Toggle single selection
  const toggleSelection = (productId) => {
    const newSelected = new Set(selectedChanges);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedChanges(newSelected);
  };

  return (
    <div data-testid="api-sync-tab" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{t.title}</h2>
          <p className="text-muted-foreground">{t.description}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleManualSync}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Sincronizar Ahora
          </Button>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t.addProvider}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="providers" className="w-full">
        <TabsList>
          <TabsTrigger value="providers">{t.providers}</TabsTrigger>
          <TabsTrigger value="excel-review" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            {t.excelReview}
            {uploadedExcelData?.summary?.to_change > 0 && (
              <Badge variant="secondary" className="ml-1">{uploadedExcelData.summary.to_change}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="comparison">{t.comparison}</TabsTrigger>
          <TabsTrigger value="history">{t.history}</TabsTrigger>
        </TabsList>

        {/* Providers Tab */}
        <TabsContent value="providers" className="space-y-4">
          {/* Sync Controls + Notifications */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button onClick={handleManualSync} disabled={runningManualSync} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                {runningManualSync ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                {language === 'es' ? 'Sincronizar Ahora' : 'Sync Now'}
              </Button>
              <span className="text-xs text-muted-foreground">{language === 'es' ? 'Automático cada 24h' : 'Auto every 24h'}</span>
            </div>
            {unreadCount > 0 && (
              <div className="flex items-center gap-2">
                <Badge variant="destructive">{unreadCount} {language === 'es' ? 'cambios nuevos' : 'new changes'}</Badge>
                <Button variant="ghost" size="sm" onClick={markNotificationsRead}>
                  {language === 'es' ? 'Marcar leídas' : 'Mark read'}
                </Button>
              </div>
            )}
          </div>

          {/* Last sync notifications */}
          {syncNotifications.length > 0 && syncNotifications[0].summary && (
            <Card className={`border-l-4 ${syncNotifications[0].summary.deactivated > 0 ? 'border-l-red-500 bg-red-50/50' : 'border-l-green-500 bg-green-50/50'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm">
                      {language === 'es' ? 'Última sincronización automática' : 'Last auto sync'}
                      {' — '}{new Date(syncNotifications[0].timestamp).toLocaleString('es-ES')}
                    </p>
                    <div className="flex gap-4 mt-2 text-sm">
                      <span className="text-green-700">{syncNotifications[0].summary.updated} {language === 'es' ? 'actualizados' : 'updated'}</span>
                      {syncNotifications[0].summary.deactivated > 0 && (
                        <span className="text-red-700 font-medium">{syncNotifications[0].summary.deactivated} {language === 'es' ? 'desactivados' : 'deactivated'}</span>
                      )}
                      {syncNotifications[0].summary.errors > 0 && (
                        <span className="text-amber-700">{syncNotifications[0].summary.errors} {language === 'es' ? 'errores' : 'errors'}</span>
                      )}
                    </div>
                    {syncNotifications[0].deactivated_products?.length > 0 && (
                      <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-800">
                        <strong>{language === 'es' ? 'Productos desactivados:' : 'Deactivated products:'}</strong>
                        {syncNotifications[0].deactivated_products.slice(0, 5).map((d, i) => (
                          <span key={i}> {d.domain}{i < Math.min(4, syncNotifications[0].deactivated_products.length - 1) ? ',' : ''}</span>
                        ))}
                        {syncNotifications[0].deactivated_products.length > 5 && <span> +{syncNotifications[0].deactivated_products.length - 5} más</span>}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Instructions Card */}
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Cloud className="h-5 w-5 text-amber-600" />
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-amber-900">
                    {language === 'es' ? '¿Cómo funciona?' : 'How does it work?'}
                  </h4>
                  <ol className="text-sm text-amber-800 space-y-1 list-decimal list-inside">
                    <li>{language === 'es' 
                      ? 'Añade un proveedor de API con su URL y email' 
                      : 'Add an API provider with its URL and email'}</li>
                    <li>{language === 'es' 
                      ? 'Haz clic en "Probar Conexión" para verificar que funciona' 
                      : 'Click "Test Connection" to verify it works'}</li>
                    <li>{language === 'es' 
                      ? 'Usa "Comparar Precios" para ver diferencias entre tu catálogo y la API' 
                      : 'Use "Compare Prices" to see differences between your catalog and the API'}</li>
                    <li>{language === 'es' 
                      ? 'Descarga el Excel con "Descargar Excel" para revisar manualmente' 
                      : 'Download Excel with "Download Excel" to review manually'}</li>
                    <li>{language === 'es' 
                      ? 'Ve a la pestaña "Revisión Excel" para aplicar los cambios' 
                      : 'Go to "Excel Review" tab to apply changes'}</li>
                  </ol>
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <h5 className="font-medium text-amber-900 mb-2">
                      {language === 'es' ? '¿Qué detecta la sincronización?' : 'What does the sync detect?'}
                    </h5>
                    <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                      <li>{language === 'es'
                        ? <><strong>Productos con precio cambiado</strong> — Detecta si el precio del proveedor ha bajado o subido respecto al tuyo</>
                        : <><strong>Products with price changes</strong> — Detects if the provider price went up or down vs yours</>}</li>
                      <li>{language === 'es'
                        ? <><strong>Productos eliminados/desactivados</strong> — Si un producto existía en la API y ya no aparece, se marca como desactivado</>
                        : <><strong>Deleted/deactivated products</strong> — If a product was in the API and is now gone, it gets deactivated</>}</li>
                      <li>{language === 'es'
                        ? <><strong>Productos nuevos</strong> — Detecta dominios que están en la API del proveedor pero no en tu base de datos</>
                        : <><strong>New products</strong> — Detects domains in the provider API that are not in your database</>}</li>
                    </ul>
                    <p className="text-xs text-amber-700 mt-2">
                      {language === 'es'
                        ? 'Todo se muestra en las notificaciones, el historial de sincronización y la tabla de comparación. Además puedes descargar un Excel comparativo con los productos donde tu PC es mayor que el precio API (diferencia > 15€).'
                        : 'Everything is shown in notifications, sync history, and the comparison table. You can also download a comparative Excel with products where your PC is higher than the API price (difference > 15€).'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : providers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Cloud className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">{t.noProviders}</p>
                <p className="text-muted-foreground mb-4">{t.addFirst}</p>
                <Button onClick={() => setAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t.addProvider}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {providers.map((provider) => (
                <Card key={provider.provider_id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${provider.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                          {provider.is_active ? (
                            <Cloud className="h-5 w-5 text-green-600" />
                          ) : (
                            <CloudOff className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{provider.name}</CardTitle>
                          <CardDescription className="text-sm">
                            {provider.owner_email} • {provider.products_count || 0} {t.products}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={provider.auto_sync_enabled ? 'default' : 'outline'}>
                          {provider.auto_sync_enabled ? (
                            <><Zap className="h-3 w-3 mr-1" /> Auto</>
                          ) : (
                            'Manual'
                          )}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => {
                            setProviderToDelete(provider.provider_id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestConnection(provider.provider_id)}
                      >
                        <Link className="h-4 w-4 mr-2" />
                        {t.testConnection}
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleCompare(provider.provider_id)}
                        disabled={comparing}
                      >
                        {comparing && selectedProvider === provider.provider_id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 mr-2" />
                        )}
                        {t.compare}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownloadExcel(provider.provider_id)}
                        className="bg-green-100 hover:bg-green-200 text-green-700"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Descargar Excel
                      </Button>
                      <div className="flex items-center gap-2 ml-auto">
                        <Label htmlFor={`auto-sync-${provider.provider_id}`} className="text-sm">
                          {t.autoSync}
                        </Label>
                        <Switch
                          id={`auto-sync-${provider.provider_id}`}
                          checked={provider.auto_sync_enabled}
                          onCheckedChange={(checked) => handleToggleAutoSync(provider.provider_id, checked)}
                        />
                      </div>
                    </div>
                    {provider.last_sync && (
                      <p className="text-xs text-muted-foreground mt-3">
                        {t.lastSync}: {new Date(provider.last_sync).toLocaleString()}
                        {provider.last_sync_result && ` - ${provider.last_sync_result}`}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        {/* Excel Review Tab */}
        <TabsContent value="excel-review" className="space-y-4">
          {/* Instructions */}
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-blue-900">
                    {language === 'es' ? 'Cambiar proveedor a uno más barato' : 'Switch to cheaper provider'}
                  </h4>
                  <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                    <li><strong>{language === 'es' ? 'Descarga el Excel' : 'Download Excel'}</strong> → {language === 'es' ? 'Pestaña "Proveedores" → "Descargar Excel"' : '"Providers" tab → "Download Excel"'}</li>
                    <li><strong>{language === 'es' ? 'Revisa y borra' : 'Review and delete'}</strong> → {language === 'es' ? 'Elimina filas que no quieras cambiar' : 'Remove rows you don\'t want'}</li>
                    <li><strong>{language === 'es' ? 'Sube aquí' : 'Upload here'}</strong> → {language === 'es' ? 'Verás la tabla con todos los cambios ANTES de aplicarlos' : 'You\'ll see all changes BEFORE applying'}</li>
                    <li><strong>{language === 'es' ? 'Revisa y selecciona' : 'Review and select'}</strong> → {language === 'es' ? 'Deselecciona los que no quieras, revisa precios y emails' : 'Deselect unwanted, review prices and emails'}</li>
                    <li><strong>{language === 'es' ? 'Aplica' : 'Apply'}</strong> → {language === 'es' ? 'Solo se actualizan los seleccionados' : 'Only selected are updated'}</li>
                  </ol>
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                    <strong>{language === 'es' ? 'Márgenes PVP:' : 'PVP Margins:'}</strong> 0-49€→150% | 50-69€→90% | 70-100€→70% | 101-350€→50% | 351-800€→20% | +800€→Manual
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                {language === 'es' ? 'Paso 1: Subir Excel para Revisar' : 'Step 1: Upload Excel to Review'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setUploadingExcel(true);
                    setExcelUpdateResult(null);
                    setExcelPreviewData(null);
                    setExcelSelected(new Set());
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const response = await axios.post(`${API}/api-sync/preview-excel-update`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
                      });
                      setExcelPreviewData(response.data);
                      // Select all by default
                      const allIds = response.data.rows.map(r => r.product_id).filter(Boolean);
                      setExcelSelected(new Set(allIds));
                      toast.success(`${response.data.total} productos listos para revisar`);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Error al procesar');
                    } finally {
                      setUploadingExcel(false);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }
                  }}
                />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingExcel} className="min-w-[250px]">
                  {uploadingExcel ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Procesando...</>) : (<><FileSpreadsheet className="h-4 w-4 mr-2" /> {language === 'es' ? 'Seleccionar Excel' : 'Select Excel'}</>)}
                </Button>
                {excelPreviewData && (
                  <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {excelPreviewData.total} {language === 'es' ? 'productos encontrados' : 'products found'}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preview Table */}
          {excelPreviewData && excelPreviewData.rows.length > 0 && !excelUpdateResult && (
            <Card>
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle className="flex items-center gap-2">
                    {language === 'es' ? 'Paso 2: Revisar Cambios' : 'Step 2: Review Changes'} ({excelPreviewData.total})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm"
                      onClick={() => {
                        if (excelSelected.size === excelPreviewData.rows.filter(r => r.product_id).length) {
                          setExcelSelected(new Set());
                        } else {
                          setExcelSelected(new Set(excelPreviewData.rows.map(r => r.product_id).filter(Boolean)));
                        }
                      }}>
                      {excelSelected.size === excelPreviewData.rows.filter(r => r.product_id).length
                        ? (<><X className="h-4 w-4 mr-1" />{language === 'es' ? 'Deseleccionar Todo' : 'Deselect All'}</>)
                        : (<><Check className="h-4 w-4 mr-1" />{language === 'es' ? 'Seleccionar Todo' : 'Select All'}</>)
                      }
                    </Button>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={excelSelected.size === 0 || applyingExcelChanges}
                      onClick={async () => {
                        setApplyingExcelChanges(true);
                        try {
                          const selectedProducts = excelPreviewData.rows
                            .filter(r => excelSelected.has(r.product_id))
                            .map(r => ({ product_id: r.product_id, domain: r.domain, new_pc: r.new_pc, new_email: r.new_email }));
                          const response = await axios.post(`${API}/api-sync/apply-excel-update`, { products: selectedProducts }, { timeout: 120000 });
                          setExcelUpdateResult(response.data);
                          toast.success(`${response.data.updated} productos actualizados`);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Error al aplicar');
                        } finally {
                          setApplyingExcelChanges(false);
                        }
                      }}>
                      {applyingExcelChanges
                        ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Aplicando...</>)
                        : (<><Check className="h-4 w-4 mr-2" /> {language === 'es' ? `Aplicar Cambios (${excelSelected.size})` : `Apply Changes (${excelSelected.size})`}</>)
                      }
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-auto" style={{ maxHeight: '600px' }}>
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-20 bg-slate-100">
                      <tr className="border-b">
                        <th className="p-3 text-left w-10"></th>
                        <th className="p-3 text-left">{language === 'es' ? 'Dominio' : 'Domain'}</th>
                        <th className="p-3 text-right">{language === 'es' ? 'PC Actual' : 'Current PC'}</th>
                        <th className="p-3 text-center">→</th>
                        <th className="p-3 text-right">{language === 'es' ? 'PC Nuevo' : 'New PC'}</th>
                        <th className="p-3 text-right">{language === 'es' ? 'PVP Nuevo' : 'New PVP'}</th>
                        <th className="p-3 text-left">{language === 'es' ? 'Email Actual' : 'Current Email'}</th>
                        <th className="p-3 text-center">→</th>
                        <th className="p-3 text-left">{language === 'es' ? 'Email Nuevo' : 'New Email'}</th>
                        <th className="p-3 text-left">{language === 'es' ? 'Sensibles' : 'Sensitive'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excelPreviewData.rows.map((row, idx) => {
                        const selected = excelSelected.has(row.product_id);
                        const emailChanged = row.new_email && row.new_email !== row.old_email;
                        const pcChanged = row.new_pc !== row.old_pc;
                        return (
                          <tr key={idx} className={`border-b ${selected ? 'bg-green-50' : 'bg-white hover:bg-slate-50'} ${!row.product_id ? 'opacity-40' : ''}`}>
                            <td className="p-3">
                              <Checkbox checked={selected} disabled={!row.product_id}
                                onCheckedChange={(checked) => {
                                  const next = new Set(excelSelected);
                                  if (checked) next.add(row.product_id);
                                  else next.delete(row.product_id);
                                  setExcelSelected(next);
                                }} />
                            </td>
                            <td className="p-3 font-medium">{row.domain}</td>
                            <td className={`p-3 text-right font-mono ${pcChanged ? 'text-red-600 line-through' : ''}`}>{row.old_pc}€</td>
                            <td className="p-3 text-center text-slate-400">{pcChanged ? '→' : '='}</td>
                            <td className={`p-3 text-right font-mono font-semibold ${pcChanged ? 'text-green-600' : ''}`}>{row.new_pc}€</td>
                            <td className="p-3 text-right font-mono text-blue-600">{row.new_pvp > 0 ? `${row.new_pvp}€` : 'Manual'}</td>
                            <td className="p-3 text-xs truncate max-w-[140px]" title={row.old_email}>
                              {emailChanged ? <span className="text-red-600 line-through">{row.old_email || '-'}</span> : <span>{row.old_email || '-'}</span>}
                            </td>
                            <td className="p-3 text-center text-slate-400">{emailChanged ? '→' : '='}</td>
                            <td className={`p-3 text-xs truncate max-w-[140px] ${emailChanged ? 'text-green-600 font-medium' : ''}`} title={row.new_email}>
                              {row.new_email || row.old_email || '-'}
                            </td>
                            <td className="p-3">
                              {row.sensitive_topics?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {row.sensitive_topics.map((t, i) => (
                                    <Badge key={i} variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{t}</Badge>
                                  ))}
                                </div>
                              ) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Result after applying */}
          {excelUpdateResult && (
            <Card className="border-green-300 bg-green-50">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                  <p className="font-medium text-lg">{excelUpdateResult.message}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white p-3 rounded border"><p className="text-2xl font-bold text-green-600">{excelUpdateResult.updated}</p><p className="text-xs text-muted-foreground">Actualizados</p></div>
                  <div className="bg-white p-3 rounded border"><p className="text-2xl font-bold text-red-600">{excelUpdateResult.errors}</p><p className="text-xs text-muted-foreground">Errores</p></div>
                </div>
                {excelUpdateResult.sample?.length > 0 && (
                  <div className="overflow-auto max-h-48">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100"><tr><th className="p-2 text-left">Dominio</th><th className="p-2 text-right">PC Anterior</th><th className="p-2 text-right">PC Nuevo</th><th className="p-2 text-right">PVP Nuevo</th><th className="p-2 text-left">Email Nuevo</th></tr></thead>
                      <tbody>
                        {excelUpdateResult.sample.map((s, i) => (
                          <tr key={i} className="border-t"><td className="p-2">{s.domain}</td><td className="p-2 text-right text-red-600 line-through">{s.old_pc}€</td><td className="p-2 text-right text-green-600 font-medium">{s.new_pc}€</td><td className="p-2 text-right text-blue-600">{s.new_pvp}€</td><td className="p-2 truncate max-w-[150px]">{s.new_email}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={() => { setExcelUpdateResult(null); setExcelPreviewData(null); setExcelSelected(new Set()); }}>
                  {language === 'es' ? 'Subir otro Excel' : 'Upload another'}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Comparison Tab */}
        <TabsContent value="comparison" className="space-y-4">
          {!comparison ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ArrowUpDown className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Selecciona un proveedor y haz clic en "Comparar Precios"</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-red-600" />
                      <div>
                        <p className="text-2xl font-bold text-red-600">{comparison.summary.more_expensive_count}</p>
                        <p className="text-xs text-red-600">{t.moreExpensive}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-2xl font-bold text-green-600">{comparison.summary.cheaper_count}</p>
                        <p className="text-xs text-green-600">{t.cheaper}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Minus className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="text-2xl font-bold text-gray-600">{comparison.summary.same_price_count}</p>
                        <p className="text-xs text-gray-600">{t.samePrice}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Plus className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{comparison.summary.only_in_api_count}</p>
                        <p className="text-xs text-blue-600">{t.onlyInApi}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      <div>
                        <p className="text-2xl font-bold text-amber-600">€{comparison.summary.potential_savings}</p>
                        <p className="text-xs text-amber-600">{t.potentialSavings}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick actions: 3 separate Excel downloads */}
              <Card>
                <CardContent className="p-4 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium mr-2">
                    {language === 'es' ? 'Descargar Excel:' : 'Download Excel:'}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => handleDownloadExcel(selectedProvider, 'expensive')}
                    data-testid="download-excel-expensive"
                    disabled={!selectedProvider}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t.moreExpensive} ({comparison.summary.more_expensive_count})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-green-300 text-green-700 hover:bg-green-50"
                    onClick={() => handleDownloadExcel(selectedProvider, 'cheaper')}
                    data-testid="download-excel-cheaper"
                    disabled={!selectedProvider}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t.cheaper} ({comparison.summary.cheaper_count})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    onClick={() => handleDownloadExcel(selectedProvider, 'missing')}
                    data-testid="download-excel-missing"
                    disabled={!selectedProvider}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Que no tengo' : "What I don't have"} ({comparison.summary.only_in_api_count})
                  </Button>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {language === 'es'
                      ? 'Formato: 32 columnas del importador estándar (PVP automático)'
                      : 'Format: 32-column standard importer (auto PVP)'}
                  </span>
                </CardContent>
              </Card>

              {/* More Expensive Table */}
              {comparison.more_expensive.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-red-600 flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        {t.moreExpensive} ({comparison.more_expensive.length})
                      </CardTitle>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="change-owner"
                            checked={changeOwner}
                            onCheckedChange={setChangeOwner}
                          />
                          <Label htmlFor="change-owner" className="text-sm">{t.changeOwnerToo}</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="enable-sync"
                            checked={enableSync}
                            onCheckedChange={setEnableSync}
                          />
                          <Label htmlFor="enable-sync" className="text-sm">{t.markForSync}</Label>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSelectAll(comparison.more_expensive)}
                        >
                          {selectedChanges.size === comparison.more_expensive.length ? (
                            <X className="h-4 w-4 mr-2" />
                          ) : (
                            <Check className="h-4 w-4 mr-2" />
                          )}
                          {t.selectAll}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleApplyChanges}
                          disabled={selectedChanges.size === 0 || applyingChanges}
                        >
                          {applyingChanges ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 mr-2" />
                          )}
                          {t.applySelected} ({selectedChanges.size})
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>{t.domain}</TableHead>
                            <TableHead className="text-right">{t.yourPrice}</TableHead>
                            <TableHead className="text-right">{t.apiPrice}</TableHead>
                            <TableHead className="text-right">{t.difference}</TableHead>
                            <TableHead>{t.currentOwner}</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comparison.more_expensive.map((item) => (
                            <TableRow 
                              key={item.product_id}
                              className={selectedChanges.has(item.product_id) ? 'bg-red-50' : ''}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedChanges.has(item.product_id)}
                                  onCheckedChange={() => toggleSelection(item.product_id)}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{item.domain}</TableCell>
                              <TableCell className="text-right font-medium">€{item.local_price}</TableCell>
                              <TableCell className="text-right text-green-600 font-medium">€{item.api_price}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="destructive">
                                  <ArrowDown className="h-3 w-3 mr-1" />
                                  €{item.difference} ({item.difference_percent}%)
                                </Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm truncate max-w-[150px]">
                                {item.current_owner || '-'}
                              </TableCell>
                              <TableCell>
                                {item.is_synced && (
                                  <Badge variant="outline" className="text-xs">
                                    <Zap className="h-3 w-3 mr-1" />
                                    Sync
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Cheaper Table */}
              {comparison.cheaper.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-green-600 flex items-center gap-2">
                      <TrendingDown className="h-5 w-5" />
                      {t.cheaper} ({comparison.cheaper.length})
                    </CardTitle>
                    <CardDescription>Estos productos ya los tienes más baratos - mantener con el dueño actual</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[300px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t.domain}</TableHead>
                            <TableHead className="text-right">{t.yourPrice}</TableHead>
                            <TableHead className="text-right">{t.apiPrice}</TableHead>
                            <TableHead className="text-right">{t.difference}</TableHead>
                            <TableHead>{t.currentOwner}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comparison.cheaper.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{item.domain}</TableCell>
                              <TableCell className="text-right text-green-600 font-medium">€{item.local_price}</TableCell>
                              <TableCell className="text-right font-medium">€{item.api_price}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="secondary" className="bg-green-100 text-green-700">
                                  <ArrowUp className="h-3 w-3 mr-1" />
                                  €{Math.abs(item.difference)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm truncate max-w-[150px]">
                                {item.current_owner || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Missing Table — products only in API, with import buttons */}
              {comparison.only_in_api && comparison.only_in_api.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <CardTitle className="text-blue-600 flex items-center gap-2">
                          <Plus className="h-5 w-5" />
                          {language === 'es' ? 'Medios que no tengo' : "Media I don't have"}
                          {' '}({comparison.only_in_api.length}
                          {comparison.summary.only_in_api_count > comparison.only_in_api.length
                            ? ` ${language === 'es' ? 'mostrados de' : 'shown of'} ${comparison.summary.only_in_api_count}`
                            : ''})
                        </CardTitle>
                        <CardDescription>
                          {language === 'es'
                            ? 'Productos en la API del proveedor que no están en tu catálogo. La categoría se mapea a las tuyas; si no se encuentra y activas IA, se intenta clasificar.'
                            : 'Products in the provider API that are not in your catalog. Category is mapped to yours; if not found and AI is enabled, it tries to classify.'}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleImportMissing(selectedProvider, null, { use_ai: true, enable_sync: true })}
                          disabled={importingMissing || !selectedProvider}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          data-testid="import-all-missing-btn"
                        >
                          {importingMissing ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4 mr-2" />
                          )}
                          {language === 'es' ? 'Añadir TODOS' : 'Import ALL'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t.domain}</TableHead>
                            <TableHead>{language === 'es' ? 'Categoría (mapeada)' : 'Category (mapped)'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'País' : 'Country'}</TableHead>
                            <TableHead className="text-right">{t.apiPrice}</TableHead>
                            <TableHead className="text-right">DR</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'Tráfico' : 'Traffic'}</TableHead>
                            <TableHead className="text-right">{language === 'es' ? 'Acción' : 'Action'}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comparison.only_in_api.map((item, idx) => (
                            <TableRow key={`missing-${idx}-${item.domain}`}>
                              <TableCell className="font-medium">{item.domain}</TableCell>
                              <TableCell>
                                {item.category ? (
                                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                                    {item.category}
                                  </Badge>
                                ) : item.category_api_raw ? (
                                  <span className="text-xs text-muted-foreground italic">
                                    {language === 'es' ? 'API: ' : 'API: '}{item.category_api_raw} →{' '}
                                    {language === 'es' ? 'sin match' : 'no match'}
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground italic">
                                    {language === 'es' ? '(sin categoría — IA al importar)' : '(no category — AI on import)'}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right text-sm">{item.country || '-'}</TableCell>
                              <TableCell className="text-right font-medium">€{item.api_price}</TableCell>
                              <TableCell className="text-right">{item.dr || 0}</TableCell>
                              <TableCell className="text-right">{item.organic_traffic || 0}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleImportMissing(selectedProvider, [item.domain], { use_ai: true, enable_sync: true })}
                                  disabled={importingMissing}
                                  className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                                  data-testid={`import-missing-${item.domain}`}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  {language === 'es' ? 'Añadir' : 'Add'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Historial de Sincronizaciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              {syncHistory.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No hay historial de sincronizaciones
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Proveedor</TableHead>
                      <TableHead className="text-right">Productos</TableHead>
                      <TableHead className="text-right">Errores</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncHistory.map((entry, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{new Date(entry.timestamp).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant={entry.type === 'automatic' ? 'default' : 'outline'}>
                            {entry.type === 'automatic' ? 'Automático' : 'Manual'}
                          </Badge>
                        </TableCell>
                        <TableCell>{entry.provider_name || '-'}</TableCell>
                        <TableCell className="text-right">{entry.products_updated}</TableCell>
                        <TableCell className="text-right">
                          {entry.errors > 0 ? (
                            <Badge variant="destructive">{entry.errors}</Badge>
                          ) : (
                            <Badge variant="secondary">0</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Provider Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t.addProvider}</DialogTitle>
            <DialogDescription>
              Configura una nueva API de proveedor para comparar y sincronizar precios
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t.name} *</Label>
              <Input
                id="name"
                placeholder="Ej: 1ereplace, Getlinko..."
                value={newProvider.name}
                onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="api_url">{t.apiUrl} *</Label>
              <Input
                id="api_url"
                placeholder="https://api.example.com/catalog"
                value={newProvider.api_url}
                onChange={(e) => setNewProvider({ ...newProvider, api_url: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="owner_email">{t.ownerEmail} *</Label>
              <Input
                id="owner_email"
                type="email"
                placeholder="contacto@proveedor.com"
                value={newProvider.owner_email}
                onChange={(e) => setNewProvider({ ...newProvider, owner_email: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Este email se asignará como dueño cuando cambies productos a este proveedor
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleAddProvider}>
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.confirmDelete}</AlertDialogTitle>
            <AlertDialogDescription>{t.confirmDeleteDesc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProvider} className="bg-destructive text-destructive-foreground">
              {t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default APISyncTab;
